import { useEffect, useLayoutEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { SunProtectionHeader } from "@/components/organisms/SunProtectionHeader/SunProtectionHeader";
import { InlineSignature } from "@/components/molecules/InlineSignature/InlineSignature";
import { SelectedWindowsList } from "@/pages/sun-protection/steps/confirm/SelectedWindowsList";
import { PriceSummary } from "@/pages/sun-protection/steps/confirm/PriceSummary";
import { ConfirmSuccess } from "@/pages/sun-protection/steps/confirm/ConfirmSuccess";
import { useConfirmRows } from "@/pages/sun-protection/steps/confirm/useConfirmRows";
import { useTotalPrice } from "@/models/price";
import { useSunProtectionPageContent } from "@/models/sun-protection-settings";
import { useDealProducts } from "@/models/deal";
import {
    getSunProtectionOrderRole,
    mapCombosToProductInputs,
    useOrderById,
} from "@/models/order";
import { useSessionStore } from "@/store/session";
import { useOrderStore } from "@/store/order";
import { getAllConfiguredCombos, useSelectionStore } from "@/store/selection";

export function OrderConfirmationPage() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const orderIdParam = searchParams.get("sunProtectionOrderId");
    const dealId = searchParams.get("dealId") ?? "";
    const hubspotDealId = dealId ? Number(dealId) : null;
    const hubspotDealIdValid =
        hubspotDealId !== null && Number.isFinite(hubspotDealId);

    const initSession = useSessionStore((s) => s.init);
    const discountCode = useSessionStore((s) => s.discountCode);
    const backendDealId = useSessionStore((s) => s.backendDealId);
    const isSubmitting = useOrderStore((s) => s.isSubmitting);
    const signatureUrl = useOrderStore((s) => s.signatureUrl);
    const submitErrorKey = useOrderStore((s) => s.error);
    const submit = useOrderStore((s) => s.submit);
    const resetForSignaturePage = useOrderStore((s) => s.resetForSignaturePage);

    const combosCount = useSelectionStore(
        (s) => getAllConfiguredCombos(s).length,
    );

    useEffect(() => {
        resetForSignaturePage();
    }, [resetForSignaturePage]);

    useLayoutEffect(() => {
        if (!dealId) return;
        if (useSessionStore.getState().dealId === dealId) return;
        initSession({ dealId, email: "", discountCode: "" });
    }, [dealId, initSession]);

    const { loading: dealLoading } = useDealProducts();
    const { loading: orderLoading, error } = useOrderById({
        orderId: orderIdParam,
        hubspotDealId: hubspotDealIdValid ? hubspotDealId : null,
    });
    const loading = dealLoading || orderLoading;
    const { totals } = useTotalPrice();
    const { groupedEntries, totalProductCount } = useConfirmRows();
    const { data: pageContent } = useSunProtectionPageContent();

    const [localSignature, setLocalSignature] = useState("");

    async function handleConfirm() {
        if (!localSignature || backendDealId == null) return;
        const combos = getAllConfiguredCombos(useSelectionStore.getState());
        if (combos.length === 0) return;
        await submit({
            dealId: backendDealId,
            productInputs: mapCombosToProductInputs(combos),
            signatureDataUrl: localSignature,
            discountCode: discountCode || undefined,
            role: getSunProtectionOrderRole(searchParams.get("returnKey")),
        });
    }

    if (!orderIdParam || !hubspotDealIdValid) {
        return (
            <div className="min-h-screen bg-gray-50">
                <SunProtectionHeader />
                <main className="mx-auto max-w-6xl px-4 py-12">
                    <p
                        data-testid="order-load-error"
                        className="text-sm text-red-500"
                    >
                        {t("orderConfirmationPage.loadingError")}
                    </p>
                </main>
            </div>
        );
    }

    const confirmDisabled =
        !localSignature ||
        isSubmitting ||
        backendDealId == null ||
        combosCount === 0;

    if (!loading && signatureUrl) {
        return (
            <div className="min-h-screen bg-gray-50">
                <SunProtectionHeader />
                <main className="mx-auto max-w-6xl px-4 py-6 pb-12">
                    <ConfirmSuccess />
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <SunProtectionHeader />
            <main className="mx-auto max-w-6xl px-4 py-6 pb-12">
                <div className="flex flex-col gap-6">
                    <h1 className="text-3xl font-bold text-gray-900">
                        {t("orderConfirmationPage.title")}
                    </h1>

                    {loading && (
                        <p className="text-sm text-gray-500">
                            {t("orderConfirmationPage.loading")}
                        </p>
                    )}

                    {error && (
                        <p
                            data-testid="order-load-error"
                            className="text-sm text-red-500"
                        >
                            {t("orderConfirmationPage.loadingError")}
                        </p>
                    )}

                    {!loading && !error && (
                        <>
                            <InlineSignature
                                onSigned={setLocalSignature}
                                onCleared={() => setLocalSignature("")}
                            />
                            <PriceSummary
                                totals={totals}
                                discountCode={discountCode}
                                totalProductCount={totalProductCount}
                                pricesIncludeVat={pageContent?.pricesIncludeVat}
                            />
                            {submitErrorKey && (
                                <p
                                    data-testid="confirm-submit-error"
                                    className="text-sm text-red-500"
                                >
                                    {t(submitErrorKey)}
                                </p>
                            )}
                            <button
                                type="button"
                                onClick={handleConfirm}
                                disabled={confirmDisabled}
                                data-testid="confirm-order-signature"
                                className="self-end rounded-full bg-primary px-6 py-3 text-sm font-semibold text-contrast-text cursor-pointer hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                            >
                                {t("orderConfirmationPage.confirm")}
                            </button>
                            <SelectedWindowsList
                                groupedEntries={groupedEntries}
                                readonly
                            />
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}
